/**
 * AdminModulesPage — HustleSuite Admin ▸ Modules.
 *
 * Toggle each module on / off. Disabling a module immediately removes it
 * from the "Other Modules" dropdown across every module page (backed by
 * the same GET /api/platform/modules endpoint the `<ModulesMenu />`
 * snippet uses).
 *
 * Endpoints:
 *   GET  /api/platform/modules/admin       — every module + enabled flag
 *   POST /api/platform/modules/{key}/toggle — flip enabled flag
 *
 * Route wired in App.js at /admin/modules.
 */
import { useEffect, useState } from "react";
import PlatformHeader from "@/components/PlatformHeader";
import { Toggle } from "@/components/ui/toggle";
import { Loader2, Grid3x3 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

export default function AdminModulesPage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/platform/modules/admin`)
      .then((r) => r.json())
      .then((d) => setModules(d.modules || []))
      .catch(() => setModules([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggle = async (key) => {
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const r = await fetch(`${API}/api/platform/modules/${key}/toggle`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      setModules((ms) => ms.map((m) => (m.key === key ? { ...m, enabled: d.enabled } : m)));
      // Clear ModulesMenu cache so users see the change on the next open.
      try { window.dispatchEvent(new CustomEvent("hs:modules-changed")); } catch (_e) { /* ignore */ }
      toast.success(`${key} → ${d.enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      <PlatformHeader
        brand={{ name: "HustleSuite Admin", to: "/admin" }}
        nav={[
          { to: "/admin",         label: "Dashboard" },
          { to: "/admin/modules", label: "Modules" },
        ]}
        showCart={false}
        showBookCta={false}
      />
      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-6">
          <Grid3x3 className="w-5 h-5 text-emerald-700 dark:text-emerald-400" strokeWidth={2} />
          <div>
            <h1 className="font-serif-display text-3xl text-stone-900 dark:text-stone-100" data-testid="admin-modules-title">
              Modules
            </h1>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Turn each Suite module on or off. Disabled modules disappear from the
              &quot;Other Modules&quot; dropdown across every module page instantly.
            </p>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-stone-500 dark:text-stone-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading modules…
          </div>
        )}

        {!loading && (
          <ul data-testid="admin-modules-list" className="rounded-xl border border-stone-200 dark:border-stone-700 overflow-hidden divide-y divide-stone-200 dark:divide-stone-700 bg-white dark:bg-stone-900">
            {modules.map((m) => (
              <li
                key={m.key}
                data-testid={`admin-module-row-${m.key}`}
                className="flex items-center gap-4 px-4 py-3"
              >
                <span
                  className="shrink-0 w-3 h-3 rounded-full"
                  style={{ backgroundColor: m.color || "#7c3aed" }}
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-stone-900 dark:text-stone-100">
                    {m.label}
                    <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400 font-normal">
                      {m.key}
                    </span>
                  </p>
                  <p className="text-[12px] text-stone-500 dark:text-stone-400 truncate">
                    {m.desc || m.home}
                  </p>
                </div>
                <Toggle
                  pressed={!!m.enabled}
                  disabled={!!busy[m.key]}
                  onPressedChange={() => toggle(m.key)}
                  data-testid={`admin-module-toggle-${m.key}`}
                  aria-label={m.enabled ? `Disable ${m.label}` : `Enable ${m.label}`}
                  className={`w-16 h-8 rounded-full text-[11px] font-bold ${
                    m.enabled
                      ? "bg-emerald-600 text-white data-[state=on]:bg-emerald-600"
                      : "bg-stone-200 dark:bg-stone-800 text-stone-600 dark:text-stone-300"
                  }`}
                >
                  {busy[m.key] ? <Loader2 className="w-4 h-4 animate-spin" /> : (m.enabled ? "ON" : "OFF")}
                </Toggle>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
